import React, { useState } from 'react';
import { exportToExcel } from '../utils/exportToExcel';
import { couponsAPI } from '../services/api';
import useApi from '../hooks/useApi';
import './Coupons.css';

const Coupons = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    valid_from: '',
    valid_until: '',
    max_uses: '',
    used_count: 0,
    status: 'active',
    min_amount: '' // Added new field
  });

  // Use API hook for coupons data
  const { data: couponsResponse = {}, loading, error, refetch } = useApi(couponsAPI.getAll);
  
  // Extract coupons array from the response structure
  const couponsArray = React.useMemo(() => {
    // Handle different possible response structures
    if (Array.isArray(couponsResponse)) {
      return couponsResponse;
    }
    if (couponsResponse && typeof couponsResponse === 'object') {
      // Check if it's the new structure with data.coupons
      if (couponsResponse.coupons && Array.isArray(couponsResponse.coupons)) {
        return couponsResponse.coupons;
      }
      // Check if it's the structure with data.data.coupons
      if (couponsResponse.data && couponsResponse.data.coupons && Array.isArray(couponsResponse.data.coupons)) {
        return couponsResponse.data.coupons;
      }
      // Check if it's directly the coupons array
      if (Array.isArray(couponsResponse.data)) {
        return couponsResponse.data;
      }
    }
    return [];
  }, [couponsResponse]);

  // Map backend coupon data to frontend format
  const mapCouponData = (coupon) => {
    // Handle both backend response formats
    const couponData = coupon.coupon || coupon;
    
    return {
      id: couponData.id,
      code: couponData.code,
      discount_type: couponData.discount_type,
      discount_value: couponData.discount_value,
      valid_from: couponData.valid_from,
      valid_until: couponData.valid_until,
      max_uses: couponData.max_uses,
      used_count: couponData.used_count || couponData.uses_count || 0,
      status: couponData.status,
      min_amount: couponData.min_amount
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare data for submission
      const submitData = { ...formData };
      
      if (editingCoupon) {
        // Update existing coupon
        await couponsAPI.update(editingCoupon.id, submitData);
      } else {
        // Add new coupon
        await couponsAPI.create(submitData);
      }
      // Refresh the data
      refetch();
      resetForm();
    } catch (err) {
      console.error('Error saving coupon:', err);
      // Handle error (show message to user)
    }
  };

  const handleEdit = (coupon) => {
    // Map coupon data to form format
    const mappedCoupon = mapCouponData(coupon);
    setEditingCoupon(coupon);
    setFormData({
      code: mappedCoupon.code || '',
      discount_type: mappedCoupon.discount_type || 'percentage',
      discount_value: mappedCoupon.discount_value || '',
      valid_from: mappedCoupon.valid_from ? mappedCoupon.valid_from.split('T')[0] : '',
      valid_until: mappedCoupon.valid_until ? mappedCoupon.valid_until.split('T')[0] : '',
      max_uses: mappedCoupon.max_uses || '',
      used_count: mappedCoupon.used_count || 0,
      status: mappedCoupon.status || 'active',
      min_amount: mappedCoupon.min_amount || '' // Added new field
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await couponsAPI.delete(id);
      // Refresh the data
      refetch();
    } catch (err) {
      console.error('Error deleting coupon:', err);
      // Handle error (show message to user)
    }
  };

  const handleStatusChange = async (couponId, newStatus) => {
    try {
      const couponToUpdate = couponsArray.find(coupon => coupon.id === couponId);
      if (couponToUpdate) {
        await couponsAPI.update(couponId, { ...mapCouponData(couponToUpdate), status: newStatus });
        // Refresh the data
        refetch();
      }
    } catch (err) {
      console.error('Error updating coupon status:', err);
      // Handle error (show message to user)
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      valid_from: '',
      valid_until: '',
      max_uses: '',
      used_count: 0,
      status: 'active',
      min_amount: '' // Added new field
    });
    setEditingCoupon(null);
    setShowForm(false);
  };

  const getDiscountDisplay = (type, value) => {
    return type === 'percentage' ? `${value}%` : `$${value}`;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'expired': return 'status-expired';
      case 'inactive': return 'status-inactive';
      default: return '';
    }
  };

  // Export function
  const exportCouponsToExcel = () => {
    // Prepare data for export
    const exportData = couponsArray.map(coupon => {
      const mappedCoupon = mapCouponData(coupon);
      return {
        ID: mappedCoupon.id || '',
        'Coupon Code': mappedCoupon.code || '',
        'Discount Type': mappedCoupon.discount_type || '',
        'Discount Value': mappedCoupon.discount_type === 'percentage' ? `${mappedCoupon.discount_value}%` : `$${mappedCoupon.discount_value}`,
        'Valid From': mappedCoupon.valid_from || '',
        'Valid Until': mappedCoupon.valid_until || '',
        'Maximum Uses': mappedCoupon.max_uses || 'Unlimited',
        'Used Count': mappedCoupon.used_count || 0,
        'Minimum Amount': mappedCoupon.min_amount ? `$${mappedCoupon.min_amount}` : '$0',
        Status: mappedCoupon.status ? mappedCoupon.status.charAt(0).toUpperCase() + mappedCoupon.status.slice(1) : ''
      };
    });
    
    exportToExcel(exportData, 'coupons', 'Coupons');
  };

  // Handle loading and error states
  if (loading) {
    return <div className="loading">Loading coupons...</div>;
  }

  if (error) {
    return <div className="error">Error loading coupons: {error.message}</div>;
  }

  return (
    <div className="coupons-page">
      <div className="page-header">
        <h1>Coupons Management</h1>
        <div>
          <button 
            className="btn btn-primary" 
            onClick={exportCouponsToExcel}
            style={{ marginRight: '10px' }}
          >
            Export to Excel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : '+ Add New Coupon'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="coupon-form-container">
          <h2>{editingCoupon ? 'Edit Coupon' : 'Add New Coupon'}</h2>
          <form onSubmit={handleSubmit} className="coupon-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="code">Coupon Code*</label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g. SAVE20"
                />
              </div>
              <div className="form-group">
                <label htmlFor="status">Status*</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="discount_type">Discount Type*</label>
                <select
                  id="discount_type"
                  name="discount_type"
                  value={formData.discount_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="discount_value">
                  Discount Value ({formData.discount_type === 'percentage' ? '%' : '$'})*
                </label>
                <input
                  type="number"
                  id="discount_value"
                  name="discount_value"
                  value={formData.discount_value}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="valid_from">Valid From*</label>
                <input
                  type="date"
                  id="valid_from"
                  name="valid_from"
                  value={formData.valid_from}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="valid_until">Valid Until*</label>
                <input
                  type="date"
                  id="valid_until"
                  name="valid_until"
                  value={formData.valid_until}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="max_uses">Maximum Uses</label>
                <input
                  type="number"
                  id="max_uses"
                  name="max_uses"
                  value={formData.max_uses}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label htmlFor="min_amount">Minimum Amount ($)</label>
                <input
                  type="number"
                  id="min_amount"
                  name="min_amount"
                  value={formData.min_amount}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {editingCoupon && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="used_count">Used Count</label>
                  <input
                    type="number"
                    id="used_count"
                    name="used_count"
                    value={formData.used_count}
                    onChange={handleInputChange}
                    min="0"
                    readOnly
                  />
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingCoupon ? 'Update Coupon' : 'Add Coupon'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="coupons-list">
        <h2>Coupons List</h2>
        <table className="coupons-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Discount</th>
              <th>Validity Period</th>
              <th>Usage</th>
              <th>Min. Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {couponsArray.map(coupon => {
              const mappedCoupon = mapCouponData(coupon);
              return (
                <tr key={mappedCoupon.id}>
                  <td>
                    <div className="coupon-code">{mappedCoupon.code || ''}</div>
                  </td>
                  <td>
                    <div className="discount-value">
                      {getDiscountDisplay(mappedCoupon.discount_type, mappedCoupon.discount_value)}
                    </div>
                  </td>
                  <td>
                    <div className="validity-period">
                      {mappedCoupon.valid_from ? mappedCoupon.valid_from.split('T')[0] : ''} to {mappedCoupon.valid_until ? mappedCoupon.valid_until.split('T')[0] : ''}
                    </div>
                  </td>
                  <td>
                    <div className="usage-info">
                      {mappedCoupon.used_count || 0} / {mappedCoupon.max_uses || '∞'}
                    </div>
                    {mappedCoupon.max_uses > 0 && (
                      <div className="usage-bar">
                        <div 
                          className="usage-progress" 
                          style={{ width: `${((mappedCoupon.used_count || 0) / mappedCoupon.max_uses) * 100}%` }}
                        ></div>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="min-amount">
                      {mappedCoupon.min_amount ? `$${mappedCoupon.min_amount}` : '$0'}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(mappedCoupon.status)}`}>
                      {mappedCoupon.status ? mappedCoupon.status.charAt(0).toUpperCase() + mappedCoupon.status.slice(1) : ''}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn btn-small btn-edit"
                        onClick={() => handleEdit(coupon)}
                      >
                        Edit
                      </button>
                      {mappedCoupon.status === 'active' ? (
                        <button 
                          className="btn btn-small btn-delete"
                          onClick={() => handleStatusChange(coupon.id, 'inactive')}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button 
                          className="btn btn-small btn-primary"
                          onClick={() => handleStatusChange(coupon.id, 'active')}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {couponsArray.length === 0 && (
          <div className="no-coupons">
            <p>No coupons found. Add your first coupon to get started!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Coupons;